import debug from 'debug';
import SourceTileWMS from 'ol/source/TileWMS';
import XYZ from 'ol/source/XYZ';
import SourceWMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import LayerTile from 'ol/layer/Tile';
import SourceTile from 'ol/source/Tile';
import { get as projGet } from 'ol/proj';

import { fromRecord } from '../locale';
import { IMapBaseLayer, IServiceBaseLayer, MessageRecord } from '../source';
import { IMapBaseLayerTranslated, hashMapBaseLayer } from '../util';
import { fromNullable } from 'fp-ts/lib/Option';
import { TileCoord } from 'ol/tilecoord';
import { iife } from '../lib';

const logger = debug('sdi:map/baselayer');

export const translateMapBaseLayer = (
    baselayerName: MessageRecord,
    l: IServiceBaseLayer,
): IMapBaseLayerTranslated => ({
    name: fromRecord(baselayerName),
    srs: l.srs,
    params: {
        LAYERS: fromRecord(l.params.LAYERS),
        VERSION: l.params.VERSION,
    },
    url: l.url,
});

const fromWMSBaseLayer = (
    baseLayerName: MessageRecord,
    service_layer: IServiceBaseLayer,
) => {
    const serviceLayerTranslated = translateMapBaseLayer(
        baseLayerName,
        service_layer,
    );
    const layer = new LayerTile<SourceTile>({
        source: new SourceTileWMS({
            projection: projGet(serviceLayerTranslated.srs) || 'EPSG:3857',
            params: {
                ...serviceLayerTranslated.params,
            },
            url: serviceLayerTranslated.url,
            // https://developer.mozilla.org/en-US/docs/Web/HTML/How_to/CORS_enabled_image
            crossOrigin: 'Anonymous',
        }),
    });
    layer.set('id', hashMapBaseLayer(baseLayerName, service_layer));
    layer.setZIndex(0);
    return layer;
};

const parser = new WMTSCapabilities();
const getWMTSCapabilities = (url: string, version: string) =>
    fetch(`${url}?service=wmts&request=GetCapabilities&version=${version}`)
        .then(response => response.text())
        .then(capabilitiesText => parser.read(capabilitiesText))
        .catch(err => logger(`Failed to load capabilities at ${url}`, err));

const capabilitiesCache: Record<string, WMTSCapabilities> = {};

const setWMTSSource = (
    layer: LayerTile<SourceTile>,
    capabilities: WMTSCapabilities,
    config: Record<string, string>,
) =>
    fromNullable(optionsFromCapabilities(capabilities, config))
        .map(options => {
            logger('WMTS Otpions', options);
            layer.setSource(
                new SourceWMTS({ ...options, crossOrigin: 'Anonymous' }),
            );
        })
        .getOrElseL(() => logger('Failed to build WMTS Otpions', config));

const fromWMTSBaseLayer = (
    baseLayerName: MessageRecord,
    service_layer: IServiceBaseLayer,
) => {
    const hash = hashMapBaseLayer(baseLayerName, service_layer);
    const serviceLayerTranslated = translateMapBaseLayer(
        baseLayerName,
        service_layer,
    );
    // const projection = projGet(baseLayerTranslated.srs) || EPSG31370;

    const layer = new LayerTile<SourceTile>({});
    if (hash in capabilitiesCache) {
        setWMTSSource(layer, capabilitiesCache[hash], {
            layer: serviceLayerTranslated.params.LAYERS,
            matrixSet: serviceLayerTranslated.srs,
        });
    } else {
        getWMTSCapabilities(
            serviceLayerTranslated.url,
            serviceLayerTranslated.params.VERSION,
        ).then(capabilities => {
            capabilitiesCache[hash] = capabilities;
            setWMTSSource(layer, capabilities, {
                layer: serviceLayerTranslated.params.LAYERS,
                matrixSet: serviceLayerTranslated.srs,
            });
        });
    }
    layer.set('id', hash);
    layer.setZIndex(0);
    return layer;
};

const fromOSMBaseLayer = (
    baseLayerName: MessageRecord,
    service_layer: IServiceBaseLayer,
) => {
    const serviceLayerTranslated = translateMapBaseLayer(
        baseLayerName,
        service_layer,
    );

    const projection = projGet(serviceLayerTranslated.srs) || 'EPSG:3857';

    const origin = serviceLayerTranslated.url;
    const parts = serviceLayerTranslated.params.LAYERS.split('/').filter(
        part => part.length > 0,
    );

    const baseUrl = iife(() => {
        if (parts.length > 0) {
            return [
                origin.replace(/\/+$/, ''),
                parts.map(p => `${p}/`).join(''),
            ].join('/');
        } else {
            return origin;
        }
    });

    const tileUrlFunction = (tc: TileCoord) => {
        const zxy = tc.map(comp => comp.toString()).join('/');
        const url = new URL(`${zxy}.png`, baseUrl);
        return url.toString();
    };

    const layer = new LayerTile<SourceTile>({
        source: new XYZ({
            projection,
            tileUrlFunction,
            crossOrigin: 'Anonymous',
        }),
    });
    layer.set('id', hashMapBaseLayer(baseLayerName, service_layer));
    layer.setZIndex(0);
    return layer;
};

export const fromBaseLayer = (
    baseLayer: IMapBaseLayer,
    layer: IServiceBaseLayer,
) => {
    switch (layer.serviceType) {
        case 'wms':
            return fromWMSBaseLayer(baseLayer.name, layer);
        case 'wmts':
            return fromWMTSBaseLayer(baseLayer.name, layer);
        case 'osm':
            return fromOSMBaseLayer(baseLayer.name, layer);
    }
};
logger('loaded');
